import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  MinusIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useQNetData } from '../contexts/QNetDataContext';

/**
 * 합격률 차트 컴포넌트
 * Q-Net API에서 합격률 데이터를 가져와 연도별 비교와 함께 표시합니다.
 */
const PassRateChart = ({ examCode, className = '' }) => {
  const [passRateData, setPassRateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!examCode) return;

    const loadBatchPassRateData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 배치 시스템에서 생성된 합격률 데이터 로드
        const response = await fetch('/qnet-pass-rates.json');
        if (!response.ok) {
          throw new Error('배치 합격률 데이터 로드 실패');
        }
        
        const batchData = await response.json();
        const examPassRateData = batchData.passRates[examCode] || batchData.passRates[examCode.toString()];
        
        if (examPassRateData) {
          // 배치 데이터를 컴포넌트에서 사용할 수 있는 형태로 변환
          const transformedData = {
            currentYear: new Date().getFullYear(),
            currentRate: examPassRateData.averagePassRate || 0,
            previousRate: examPassRateData.recentYears?.[1]?.passRate || 0,
            yearlyData: examPassRateData.recentYears?.map(yearData => ({
              year: parseInt(yearData.year),
              rate: yearData.passRate,
              applicants: yearData.applicants ?? 0, // 실데이터(응시자수)
              passed: yearData.passedCount ?? 0     // 실데이터(합격자수)
            })) || []
          };

          setPassRateData(transformedData);
        } else {
          // 실데이터 없는 자격증: 가짜(mock) 대신 '데이터 없음'으로 표시 (큐넷 안내)
          setPassRateData(null);
        }
        
      } catch (err) {
        console.error('배치 합격률 데이터 로드 실패:', err);
        // 가짜(mock) 대신 '데이터 없음' 처리
        setPassRateData(null);
      } finally {
        setLoading(false);
      }
    };

    loadBatchPassRateData();
  }, [examCode]);

  // 실제 API 데이터 파싱 함수 (미래에 API가 제공될 때 사용)
  const parseRealPassRateData = (passRateApiData) => {
    // 실제 Q-Net API에서 합격률 데이터를 제공할 때 여기서 파싱
    // 현재는 미구현 상태
    return passRateApiData;
  };

  // API 응답에서 합격률 데이터 파싱
  const parsePassRateData = (apiData) => {
    // Q-Net API는 passRate 정보를 제공하지 않음 (null로 반환)
    if (apiData && apiData.passRate) {
      // 실제 API에서 합격률 데이터가 제공되는 경우 (현재는 해당 없음)
      return parseRealPassRateData(apiData.passRate);
    }
    
    // API에서 합격률 데이터를 제공하지 않으므로 null 반환
    return null;
  };

  // 개발용 mock 데이터 (실제 API 데이터로 대체 예정)
  const getMockPassRateData = (code) => {
    const mockData = {
      '1150': { // 전기기사
        currentYear: 2024,
        currentRate: 28.5,
        previousRate: 31.2,
        yearlyData: [
          { year: 2020, rate: 35.8, applicants: 18420, passed: 6590 },
          { year: 2021, rate: 33.1, applicants: 19850, passed: 6570 },
          { year: 2022, rate: 29.7, applicants: 21340, passed: 6340 },
          { year: 2023, rate: 31.2, applicants: 20180, passed: 6300 },
          { year: 2024, rate: 28.5, applicants: 22450, passed: 6400 }
        ]
      },
      '1320': { // 정보처리기사
        currentYear: 2024,
        currentRate: 42.8,
        previousRate: 39.6,
        yearlyData: [
          { year: 2020, rate: 45.2, applicants: 42100, passed: 19030 },
          { year: 2021, rate: 41.7, applicants: 48300, passed: 20140 },
          { year: 2022, rate: 38.9, applicants: 52400, passed: 20390 },
          { year: 2023, rate: 39.6, applicants: 54200, passed: 21460 },
          { year: 2024, rate: 42.8, applicants: 56800, passed: 24310 }
        ]
      }
    };

    return mockData[code] || {
      currentYear: 2024,
      currentRate: 35.0,
      previousRate: 33.5,
      yearlyData: [
        { year: 2023, rate: 33.5, applicants: 15000, passed: 5025 },
        { year: 2024, rate: 35.0, applicants: 16200, passed: 5670 }
      ]
    };
  };

  const getTrendIcon = (current, previous) => {
    const diff = current - previous;
    if (Math.abs(diff) < 0.5) {
      return <MinusIcon className="w-4 h-4 text-gray-500" />;
    }
    return diff > 0 
      ? <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />
      : <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
  };

  const getTrendText = (current, previous) => {
    const diff = current - previous;
    const absChange = Math.abs(diff);
    
    if (absChange < 0.5) {
      return { text: '변화없음', color: 'text-gray-600' };
    }
    
    const direction = diff > 0 ? '상승' : '하락';
    const color = diff > 0 ? 'text-green-600' : 'text-red-600';
    
    return {
      text: `전년 대비 ${absChange.toFixed(1)}%p ${direction}`,
      color
    };
  };

  const getBarHeight = (rate, maxRate) => {
    return Math.max((rate / maxRate) * 100, 5); // 최소 5% 높이
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg border border-orange-100 ${className}`}>
        <div className="flex items-center mb-4">
          <ChartBarIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">합격률 통계</h3>
        </div>
        
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-500">합격률 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg border border-orange-100 ${className}`}>
        <div className="flex items-center mb-4">
          <ChartBarIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">합격률 통계</h3>
        </div>
        
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">{error}</p>
          <p className="text-sm text-gray-400 mb-3">
            Q-Net API에서 데이터를 가져올 수 없습니다.
          </p>
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors"
          >
            Q-net 공식 홈페이지에서 확인하기 →
          </a>
        </div>
      </div>
    );
  }

  if (!passRateData) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg border border-orange-100 ${className}`}>
        <div className="flex items-center mb-4">
          <ChartBarIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">합격률 통계</h3>
        </div>
        
        <div className="text-center py-8">
          <InformationCircleIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-700 mb-2">합격률 데이터 없음</h4>
          <p className="text-gray-500 mb-2">
            이 자격증의 최근 합격률 데이터가 아직 없습니다.
          </p>
          <p className="text-sm text-gray-400 mb-3">
            정확한 합격률 정보는 Q-Net 공식 홈페이지에서 확인하실 수 있습니다.
          </p>
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors"
          >
            Q-net 공식 홈페이지에서 확인하기 →
          </a>
        </div>
        
        {/* 데이터 소스 정보 */}
        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>출처: Q-Net API (합격률 미제공)</span>
            <span>업데이트: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    );
  }

  const { currentYear, currentRate, previousRate, yearlyData } = passRateData;
  const trendIcon = getTrendIcon(currentRate, previousRate);
  const trendText = getTrendText(currentRate, previousRate);
  const maxRate = Math.max(...yearlyData.map(d => d.rate));
  const scaledMaxRate = Math.ceil(maxRate / 10) * 10; // 10 단위로 올림

  return (
    <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ChartBarIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">합격률 통계</h3>
        </div>
        
        <div className="flex items-center">
          {trendIcon}
          <span className={`ml-2 text-sm font-medium ${trendText.color}`}>
            {trendText.text}
          </span>
        </div>
      </div>

      {/* 현재 합격률 하이라이트 */}
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 mb-6">
        <div className="text-center">
          <div className="text-sm text-orange-600 font-medium mb-2">{currentYear}년 합격률</div>
          <div className="text-4xl font-bold text-gray-900 mb-2">
            {currentRate.toFixed(1)}%
          </div>
          <div className={`text-sm ${trendText.color}`}>
            {trendText.text}
          </div>
        </div>
      </div>

      {/* 연도별 차트 */}
      <div className="mb-6">
        <div className="flex items-end justify-between h-48 mb-4 bg-gray-50 rounded-lg p-4">
          {yearlyData.map((data, index) => (
            <div key={data.year} className="flex flex-col items-center flex-1 mx-1">
              <div className="relative w-full max-w-12 bg-gray-200 rounded-t-md overflow-hidden">
                <div 
                  className="bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-md transition-all duration-500 delay-200"
                  style={{ 
                    height: `${getBarHeight(data.rate, scaledMaxRate)}%`,
                    minHeight: '8px'
                  }}
                >
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                    {data.rate}%
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-600 font-medium">
                {data.year}
              </div>
            </div>
          ))}
        </div>
        
        {/* Y축 라벨 */}
        <div className="flex justify-between text-xs text-gray-500 mb-2">
          <span>0%</span>
          <span>{(scaledMaxRate / 2).toFixed(0)}%</span>
          <span>{scaledMaxRate}%</span>
        </div>
      </div>

      {/* 상세 통계 */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600 font-medium mb-1">최고 합격률</div>
          <div className="text-lg font-bold text-blue-900">
            {Math.max(...yearlyData.map(d => d.rate)).toFixed(1)}%
          </div>
          <div className="text-xs text-blue-600">
            {yearlyData.find(d => d.rate === Math.max(...yearlyData.map(d => d.rate)))?.year}년
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-green-600 font-medium mb-1">평균 합격률</div>
          <div className="text-lg font-bold text-green-900">
            {(yearlyData.reduce((sum, d) => sum + d.rate, 0) / yearlyData.length).toFixed(1)}%
          </div>
          <div className="text-xs text-green-600">
            최근 {yearlyData.length}년간
          </div>
        </div>
      </div>

      {/* 분석 인사이트 */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <InformationCircleIcon className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-indigo-800 mb-2">분석 인사이트</div>
            <div className="text-sm text-indigo-700">
              {currentRate > 40 
                ? '상대적으로 높은 합격률을 보이는 자격증입니다. 꾸준한 학습으로 합격 가능성이 높습니다.'
                : currentRate > 25 
                ? '보통 수준의 합격률을 보입니다. 체계적인 학습 계획이 중요합니다.'
                : '상대적으로 낮은 합격률을 보이는 자격증입니다. 철저한 준비가 필요합니다.'
              }
            </div>
          </div>
        </div>
      </div>

      {/* 데이터 소스 정보 */}
      <div className="pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>출처: Q-Net API (배치 시스템)</span>
          <span>업데이트: {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

export default PassRateChart;